import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { FolderStructure, ImageData } from '../types';
import { processZipFile } from '../utils/zipUtils';
import {
  generateProjectId,
  uploadFile,
  saveProjectMetadata,
  getProjectMetadata,
  listProjects,
  getDownloadUrl,
  deleteProject
} from '../utils/r2Storage';

const UploaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2rem;
  border: 2px dashed #ccc;
  border-radius: 8px;
  background-color: #f9f9f9;
  margin-bottom: 2rem;
  
  &.drag-active {
    border-color: #2684ff;
    background-color: #ebf5ff;
  }
`;

const UploadButton = styled.button`
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
  cursor: pointer;
  margin-top: 1rem;
  
  &:hover {
    background-color: #0069d9;
  }
  
  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }
`;

const FileInput = styled.input`
  display: none;
`;

const UploadText = styled.p`
  font-size: 1.2rem;
  margin-bottom: 1rem;
`;

const FileInfo = styled.div`
  margin-top: 1rem;
  text-align: center;
`;

const ErrorMessage = styled.div`
  color: #d9534f;
  padding: 1rem;
  margin-top: 1rem;
  background-color: #f9f2f2;
  border-radius: 4px;
  border: 1px solid #d9534f;
  width: 100%;
  text-align: center;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 20px;
  background-color: #e0e0e0;
  border-radius: 10px;
  margin-top: 1rem;
  overflow: hidden;
`;

const ProgressFill = styled.div<{ progress: number }>`
  height: 100%;
  width: ${props => props.progress}%;
  background-color: #28a745;
  transition: width 0.3s ease;
`;

const ProgressText = styled.p`
  font-size: 0.9rem;
  color: #666;
  margin-top: 0.5rem;
`;

const ProjectsSection = styled.div`
  width: 100%;
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid #ddd;
`;

const ProjectsTitle = styled.h3`
  font-size: 1.1rem;
  color: #333;
  margin-bottom: 1rem;
  text-align: center;
`;

const ProjectsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  justify-content: center;
`;

const ProjectItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  width: 100%;
`;

const ProjectButton = styled.button`
  background-color: #6c757d;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 0.5rem 1rem;
  font-size: 0.9rem;
  cursor: pointer;
  flex: 1;
  text-align: left;
  display: flex;
  align-items: center;
  
  &:hover {
    background-color: #5a6268;
  }
  
  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }
`;

const DeleteButton = styled.button`
  background: #fff3cd;
  color: #856404;
  border: 1px solid #ffeeba;
  width: 36px;
  height: 36px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 1rem;
  transition: all 0.2s;
  flex-shrink: 0;

  &:hover {
    background: #f8d7da;
    color: #721c24;
    border-color: #f5c6cb;
  }
`;

const LoadingSpinner = styled.span`
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid #fff;
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-right: 0.5rem;
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

interface FileUploaderProps {
  onZipProcessed?: (folderStructure: FolderStructure, images: ImageData[], projectId: string) => void;
}

interface ProjectInfo {
  id: string;
}

const FileUploader: React.FC<FileUploaderProps> = ({ onZipProcessed }) => {
  const location = useLocation();
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState({
    current: 0,
    total: 0,
    loadedBytes: 0,
    totalBytes: 0
  });

  const formatBytes = (bytes: number, decimals = 2) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  };
  const [projects, setProjects] = useState<ProjectInfo[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [loadingProjectId, setLoadingProjectId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load existing projects on mount
  useEffect(() => {
    const loadProjects = async () => {
      try {
        const projectsList = await listProjects();
        setProjects(projectsList);
      } catch (err) {
        console.error('Failed to load projects:', err);
      } finally {
        setLoadingProjects(false);
      }
    };
    loadProjects();
  }, []);
  const handleLoadProject = useCallback(async (projectId: string) => {
    setLoadingProjectId(projectId);
    setError(null);

    try {
      const metadata = await getProjectMetadata(projectId);

      if (!metadata) {
        throw new Error('Proje metadata bulunamadı');
      }

      if (!metadata.images || !Array.isArray(metadata.images)) {
        throw new Error('Proje görsel verileri eksik veya hatalı');
      }

      // Generate signed URLs for images
      const imagesWithUrls = await Promise.all(
        metadata.images.map(async (img: any) => {
          const url = await getDownloadUrl(img.r2Key);
          return { ...img, url };
        })
      );

      onZipProcessed?.(metadata.folderStructure, imagesWithUrls, projectId);
    } catch (error: any) {
      console.error('Proje yüklenirken hata:', error);
      setError(`Proje yüklenirken hata: ${error.message}`);
    } finally {
      setLoadingProjectId(null);
    }
  }, [onZipProcessed]);

  // Auto-load project from query parameter
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const projectId = params.get('project');
    if (projectId) {
      console.log('Auto-loading project:', projectId);
      handleLoadProject(projectId);
    }
  }, [location.search, handleLoadProject]);

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      if (file.type === 'application/zip' || file.name.endsWith('.zip')) {
        setSelectedFile(file);
        setError(null);
      } else {
        setError('Lütfen .zip uzantılı bir dosya yükleyin!');
      }
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      if (file.type === 'application/zip' || file.name.endsWith('.zip')) {
        setSelectedFile(file);
        setError(null);
      } else {
        setError('Lütfen .zip uzantılı bir dosya yükleyin!');
      }
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleProcessZip = async () => {
    if (!selectedFile) return;

    setIsProcessing(true);
    setError(null);

    try {
      // Step 1: Process zip locally
      const result = await processZipFile(selectedFile);

      if (!result.folderStructure || result.images.length === 0) {
        throw new Error('Zip dosyasında geçerli görsel bulunamadı. Klasör yapısının doğru olduğundan emin olun: [Kanat]/[Yüzey]/[dosya.jpg]');
      }

      // Step 2: Upload to R2 cloud storage
      setIsUploading(true);
      const projectId = generateProjectId(result.folderStructure.turbineName);

      // Calculate total size
      let totalBytes = 0;
      const blobs: Blob[] = [];

      // Pre-fetch blobs to get size
      for (const image of result.images) {
        const blob = await fetch(image.url).then(r => r.blob());
        blobs.push(blob);
        totalBytes += blob.size;
      }

      setUploadProgress({ current: 0, total: result.images.length, loadedBytes: 0, totalBytes });

      // Upload images
      const imageKeyMap = new Map<string, string>();
      let loadedBytes = 0;

      for (let i = 0; i < result.images.length; i++) {
        const image = result.images[i];
        const blob = blobs[i]; // Use pre-fetched blob
        const contentType = image.fileName.toLowerCase().endsWith('.png') ? 'image/png' : 'image/jpeg';

        const key = await uploadFile(projectId, image.path, blob, contentType);
        imageKeyMap.set(image.path, key);

        loadedBytes += blob.size;
        setUploadProgress({
          current: i + 1,
          total: result.images.length,
          loadedBytes,
          totalBytes
        });
      }

      // Step 3: Save metadata to R2
      const metadata = {
        folderStructure: result.folderStructure,
        images: result.images.map(img => ({
          ...img,
          r2Key: imageKeyMap.get(img.path),
          url: undefined, // Remove blob URL
        })),
        annotations: [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      await saveProjectMetadata(projectId, metadata);

      // Step 4: Pass to parent with cloud URLs (if callback provided)
      onZipProcessed?.(result.folderStructure, result.images, projectId);

      // Refresh projects list
      const updatedProjects = await listProjects();
      setProjects(updatedProjects);

      setIsUploading(false);
    } catch (error: any) {
      console.error('Zip dosyası işlenirken hata oluştu:', error);
      setError(`Zip dosyası işlenirken bir hata oluştu: ${error.message}`);
      setIsUploading(false);
    } finally {
      setIsProcessing(false);
    }
  };


  return (
    <UploaderContainer
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={isDragging ? 'drag-active' : ''}
    >
      <UploadText>Drag and drop zip file or select</UploadText>
      <UploadButton onClick={handleUploadClick} disabled={isProcessing || isUploading}>
        Select File
      </UploadButton>
      <FileInput
        type="file"
        accept=".zip"
        ref={fileInputRef}
        onChange={handleFileChange}
      />

      {selectedFile && (
        <FileInfo>
          <p>Selected file: <strong>{selectedFile.name}</strong></p>
          <UploadButton
            onClick={handleProcessZip}
            disabled={isProcessing || isUploading}
            style={{ marginTop: '0.5rem' }}
          >
            {isProcessing && !isUploading && 'Processing...'}
            {isUploading && 'Uploading to Cloud...'}
            {!isProcessing && !isUploading && 'Process and Upload File'}
          </UploadButton>
        </FileInfo>
      )}

      {isUploading && uploadProgress.total > 0 && (
        <>
          <ProgressBar>
            <ProgressFill progress={(uploadProgress.current / uploadProgress.total) * 100} />
          </ProgressBar>
          <ProgressText>
            <strong>{uploadProgress.current} / {uploadProgress.total}</strong> images uploaded
            <br />
            ({formatBytes(uploadProgress.loadedBytes)} / {formatBytes(uploadProgress.totalBytes)})
          </ProgressText>
        </>
      )}

      {error && (
        <ErrorMessage>
          {error}
        </ErrorMessage>
      )}

      {/* Existing Projects */}
      <ProjectsSection>
        <ProjectsTitle>📁 Saved Projects</ProjectsTitle>
        {loadingProjects ? (
          <ProgressText>Loading projects...</ProgressText>
        ) : projects.length === 0 ? (
          <ProgressText>No saved projects yet</ProgressText>
        ) : (
          <ProjectsList>
            {projects.map(project => (
              <ProjectItem key={project.id}>
                <ProjectButton
                  onClick={() => handleLoadProject(project.id)}
                  disabled={loadingProjectId !== null}
                >
                  {loadingProjectId === project.id && <LoadingSpinner />}
                  {project.id}
                </ProjectButton>
                <DeleteButton
                  onClick={async (e) => {
                    e.stopPropagation();
                    if (window.confirm(`Are you sure you want to delete project ${project.id}?`)) {
                      try {
                        await deleteProject(project.id);
                        setProjects(projects.filter(p => p.id !== project.id));
                      } catch (err: any) {
                        alert('Failed to delete project: ' + err.message);
                      }
                    }
                  }}
                  title="Delete Project"
                >
                  ✕
                </DeleteButton>
              </ProjectItem>
            ))}
          </ProjectsList>
        )}
      </ProjectsSection>
    </UploaderContainer>
  );
};

export default FileUploader;